import { useEffect, useState } from 'react';
import { Card, EmptyState, PageHeader, Toggle, ConfirmDialog } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { agentApi } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';

interface VmRow {
  id: number;
  from_number: string;
  from_lookup?: unknown;
  duration: number;
  file_url?: string;
  transcript?: string;
  read_at?: string | null;
  created_at: string;
}

export function VoicemailPage() {
  const [items, setItems] = useState<VmRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const [confirmDel, setConfirmDel] = useState<VmRow | null>(null);

  useEffect(() => {
    void load();
    void agentApi.voicemailSettings().then((s) => setEnabled(s.voicemail_enabled)).catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    try {
      setItems((await agentApi.voicemails()) as VmRow[]);
    } finally {
      setLoading(false);
    }
  }

  async function toggle(next: boolean) {
    setSavingToggle(true);
    const prev = enabled;
    setEnabled(next); // optimistic
    try {
      await agentApi.setVoicemailEnabled(next);
      toast(next ? 'תא קולי הופעל' : 'תא קולי כובה', 'ok');
    } catch {
      setEnabled(prev);
      toast('שגיאה בעדכון ההגדרה', 'danger');
    } finally {
      setSavingToggle(false);
    }
  }

  async function markRead(vm: VmRow) {
    if (vm.read_at) return;
    try {
      await agentApi.markVoicemailRead(vm.id);
      setItems((rows) => rows.map((r) => (r.id === vm.id ? { ...r, read_at: new Date().toISOString() } : r)));
    } catch { /* non-blocking */ }
  }

  async function remove(vm: VmRow) {
    try {
      await agentApi.deleteVoicemail(vm.id);
      setItems((rows) => rows.filter((r) => r.id !== vm.id));
      toast('ההודעה נמחקה', 'ok');
    } catch {
      toast('שגיאה במחיקה', 'danger');
    } finally {
      setConfirmDel(null);
    }
  }

  const unread = items.filter((v) => !v.read_at).length;

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader
        title="תא קולי"
        subtitle={unread > 0 ? `${unread} הודעות שלא נשמעו` : undefined}
        actions={
          <Toggle
            checked={enabled}
            onChange={toggle}
            disabled={savingToggle}
            label={enabled ? 'קבלת הודעות פעילה' : 'קבלת הודעות כבויה'}
          />
        }
      />

      {!enabled && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-2)', fontSize: 'var(--t-sm)' }}>
            <Icon name="voicemail" size={18} color="var(--muted)" />
            המתקשרים לא יוכלו להשאיר לך הודעה קולית כל עוד האפשרות כבויה.
          </div>
        </Card>
      )}

      {loading ? (
        <Card>טוען…</Card>
      ) : items.length === 0 ? (
        <EmptyState icon="voicemail" title="אין הודעות בתא הקולי" />
      ) : (
        items.map((vm) => (
          <Card key={vm.id} style={!vm.read_at ? { borderInlineStart: '3px solid var(--accent)' } : undefined}>
            <div className="stack" style={{ gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {!vm.read_at && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} aria-label="לא נשמע" />}
                  <PhoneCell number={vm.from_number} lookup={vm.from_lookup as never} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {new Date(vm.created_at).toLocaleString('he-IL')}
                  </span>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setConfirmDel(vm)}
                    aria-label="מחק הודעה"
                    title="מחק"
                    style={{ height: 30, width: 30, padding: 0 }}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </div>
              {vm.file_url ? (
                <div onPointerDown={() => void markRead(vm)}>
                  <AudioPlayer src={vm.file_url} duration={vm.duration} />
                </div>
              ) : (
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>אין קובץ אודיו</span>
              )}
              {vm.transcript && (
                <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>{vm.transcript}</p>
              )}
            </div>
          </Card>
        ))
      )}

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => confirmDel && void remove(confirmDel)}
        title="מחיקת הודעה קולית"
        message="למחוק את ההודעה הקולית? פעולה זו אינה הפיכה."
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}
